import { Component } from "react";
import { reportarFallo } from "../lib/monitoreo";
import Icon from "./Icon";

/* =========================================================================
   LÍMITE DE ERROR
   Sin esto, un fallo de render en CUALQUIER módulo deja el panel entero en
   blanco: React desmonta el árbol completo y no queda ni el menú para irse a
   otra sección. Con esto el fallo se queda encerrado en el área de contenido y
   el resto del panel sigue usable.

   Cubre además el caso que aparece en cada despliegue: las pantallas se cargan
   bajo demanda, así que si el navegador tiene abierta una versión vieja y el
   archivo de un módulo ya no existe en el servidor, la importación falla. Eso
   se ve igual que un error de render y se arregla recargando, que es justo lo
   que ofrece el botón.

   Tiene que ser un componente de clase: los hooks no pueden capturar errores
   de render, no hay equivalente funcional a `componentDidCatch`.
   ======================================================================== */

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Queda en la consola del navegador para poder diagnosticar: el mensaje de
    // pantalla es para el usuario, esto es para quien tenga que arreglarlo.
    console.error("Fallo en un módulo del panel:", error, info?.componentStack);

    /*
     * Y se reporta. Que la persona haya visto una pantalla de error no arregla
     * nada: sin esto, el único registro del fallo es su recuerdo, y "se me
     * puso una pantalla rara" no alcanza para reproducirlo.
     */
    reportarFallo(error, {
      ruta: this.props.claveReinicio,
      componente: info?.componentStack?.slice(0, 600),
    });
  }

  /* Al navegar a otra sección se limpia el error: si no, la pantalla rota se
     quedaría puesta encima de la sección nueva, que quizá funciona bien. */
  componentDidUpdate(prevProps) {
    if (
      this.state.error &&
      prevProps.claveReinicio !== this.props.claveReinicio
    ) {
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;

    if (!error) return this.props.children;

    return (
      <div className="card">
        <div className="empty-state">
          <span className="empty-icon" style={{ color: "var(--bad)" }}>
            <Icon name="alert" size={28} />
          </span>
          <h3>Esta sección no se pudo mostrar</h3>
          <p>
            Ocurrió un error al dibujar la pantalla. El resto del panel sigue
            funcionando: puedes ir a otra sección desde el menú.
          </p>

          <div className="row gap-3" style={{ marginTop: "var(--sp-4)" }}>
            <button
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              <Icon name="refresh" size={15} /> Recargar
            </button>
            <button
              className="btn btn-outline"
              onClick={() => this.setState({ error: null })}
            >
              Reintentar sin recargar
            </button>
          </div>

          {/* El detalle técnico va plegado: le sirve a quien reporta el fallo,
              pero no debe ser lo primero que se lea. */}
          <details
            style={{
              marginTop: "var(--sp-4)",
              textAlign: "left",
              maxWidth: 560,
            }}
          >
            <summary
              className="faint"
              style={{ cursor: "pointer", fontSize: "var(--fs-xs)" }}
            >
              Detalle técnico
            </summary>
            <pre
              style={{
                marginTop: "var(--sp-2)",
                padding: "var(--sp-3)",
                background: "var(--surface-2)",
                borderRadius: "var(--r-sm)",
                fontSize: "var(--fs-micro)",
                whiteSpace: "pre-wrap",
                overflowX: "auto",
              }}
            >
              {String(error?.message ?? error)}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
